import { apiClient } from "./client";
import type {
  AttachmentDownloadResponse,
  AttachmentRead,
} from "./types";

/**
 * Polymorphic attachments client.
 *
 * The backend ships two parallel route trees — one rooted at
 * `/projects/{slug}/tasks/{id}` and one at
 * `/projects/{slug}/companies/{id}` — with identical payloads. We
 * expose ONE function per verb that branches on `entityType` so
 * callers can't accidentally point a task at a company URL (and vice
 * versa). The branching happens in a single private helper, so the
 * cross-pollination invariant is enforced in exactly one place.
 */
export type AttachmentEntityType = "task" | "company";

/** Hard caps the backend enforces; the frontend mirrors them so users
 *  hit a toast on the local side instead of a 4xx. */
export const ATTACHMENT_CAPS: Record<AttachmentEntityType, number> = {
  task: 10,
  company: 50,
};

/** Server-side hard limit on individual file size (25 MiB). Replicated
 *  here so users get a local error before paying for the upload round-
 *  trip. Keep in sync with the backend. */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/** Whitelisted mime types — must mirror the backend's allow-list.
 *  Anything outside this set is rejected client-side with a toast. */
export const ATTACHMENT_ALLOWED_MIMES: ReadonlySet<string> = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/jpeg",
  "image/png",
]);

/** Build the canonical attachments URL for one entity. Single
 *  source of truth — all four verbs route through this so an
 *  entityType change can never leak into another entity's URL. */
function urlFor(
  slug: string,
  entityType: AttachmentEntityType,
  entityId: number,
  suffix = "",
): string {
  // Reject NaN / negative ids defensively — the backend would 422
  // but the URL would otherwise look superficially valid in dev
  // tools, which makes debugging slower.
  if (!Number.isInteger(entityId) || entityId <= 0) {
    throw new Error(
      `attachments: invalid entityId ${entityId} for ${entityType}`,
    );
  }
  const root = entityType === "task" ? "tasks" : "companies";
  return `/projects/${slug}/${root}/${entityId}/attachments${suffix}`;
}

export async function listAttachments(
  slug: string,
  entityType: AttachmentEntityType,
  entityId: number,
): Promise<AttachmentRead[]> {
  const res = await apiClient.get<AttachmentRead[]>(
    urlFor(slug, entityType, entityId),
  );
  return res.data;
}

export async function uploadAttachment(
  slug: string,
  entityType: AttachmentEntityType,
  entityId: number,
  file: File,
  options?: { signal?: AbortSignal },
): Promise<AttachmentRead> {
  const form = new FormData();
  form.append("file", file);
  // The apiClient instance defaults to `application/json` — for
  // FormData uploads we have to override that header so the
  // browser/XHR layer rewrites it with the multipart boundary
  // before sending. Without this, the global JSON default would
  // win and the server would reject the request with "missing
  // boundary".
  //
  // `signal` is opt-in (default: undefined). The TaskCreateDialog
  // sequential pipeline binds a single AbortController across the
  // whole loop so the user's "Abort uploads" button can cancel
  // the in-flight request and short-circuit the queue. axios
  // rejects with a `CanceledError` (axios.isCancel === true) so
  // the caller can distinguish abort from server errors.
  const res = await apiClient.post<AttachmentRead>(
    urlFor(slug, entityType, entityId),
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      signal: options?.signal,
    },
  );
  return res.data;
}

export async function getDownloadUrl(
  slug: string,
  entityType: AttachmentEntityType,
  entityId: number,
  attachmentId: string,
): Promise<AttachmentDownloadResponse> {
  const res = await apiClient.get<AttachmentDownloadResponse>(
    urlFor(slug, entityType, entityId, `/${attachmentId}/download`),
  );
  return res.data;
}

export async function deleteAttachment(
  slug: string,
  entityType: AttachmentEntityType,
  entityId: number,
  attachmentId: string,
): Promise<void> {
  await apiClient.delete(
    urlFor(slug, entityType, entityId, `/${attachmentId}`),
  );
}

// ─── Validation helpers (shared between live + buffer modes) ─────────────

export interface AttachmentValidationError {
  file: File;
  reason: "mime" | "size" | "cap";
}

export interface AttachmentValidationResult {
  /** Files that passed all three gates and can be uploaded. */
  accepted: File[];
  /** Files that failed, with the first failing rule for each. */
  rejected: AttachmentValidationError[];
  /** True iff the cap would be reached / exceeded after accepting
   *  the `accepted` list. Callers surface a one-line toast. */
  capHit: boolean;
}

/** Pure function — given a candidate set + the existing count + the
 *  cap, partition into accepted / rejected with first-failing-rule
 *  semantics. Used by AttachmentZone for its drop handler AND by
 *  CompanyDetailPage for its page-wide drag overlay so they apply
 *  identical rules. */
export function validateAttachmentFiles(
  files: File[],
  ctx: { existingCount: number; cap: number },
): AttachmentValidationResult {
  const accepted: File[] = [];
  const rejected: AttachmentValidationError[] = [];
  let remaining = Math.max(0, ctx.cap - ctx.existingCount);
  let capHit = false;
  for (const file of files) {
    if (!ATTACHMENT_ALLOWED_MIMES.has(file.type)) {
      rejected.push({ file, reason: "mime" });
      continue;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      rejected.push({ file, reason: "size" });
      continue;
    }
    if (remaining <= 0) {
      rejected.push({ file, reason: "cap" });
      capHit = true;
      continue;
    }
    accepted.push(file);
    remaining -= 1;
  }
  return { accepted, rejected, capHit };
}
